import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '../../components';
import { Row, Col, Input, Tooltip } from 'antd';
import { DeleteOutlined, PlusOutlined, TagOutlined } from '@ant-design/icons';

const actionButtons = [
    { key: 'refreshSources', label: 'Refresh vSphere Sources' },
    { key: 'sourceDiscovery', label: 'Source Discovery', onClick: () => window.reactNavigate('source_discovery') },
    { key: 'runCSVMigration', label: 'Run CSV Migration' },
];

const { Search } = Input;

export const Actions = React.memo(
    ({
        onSearch,
        selectedRowKeys = [],
        onDelete,
        onAddSource,
        onUploadSources,
        onConfigureMigration,
        onInspectSources,
        onConfigureInSitu,
        onAddTags,
        onAssignMoveGroup,
        onRunCsvMigration,
        onRefreshSources,
    }) => {
        const [searchValue, setSearchValue] = useState('');
        const debounceRef = useRef(null);

        // Filter out group keys to get only actual source IDs
        const selectedSourceIds = selectedRowKeys.filter((key) => !key.startsWith('group-'));
        const selectedCount = selectedSourceIds.length;

        const handleSearch = useCallback(
            (value) => {
                setSearchValue(value);
                onSearch?.(value);
            },
            [onSearch],
        );

        const handleInputChange = useCallback(
            (e) => {
                const value = e.target.value;
                setSearchValue(value);

                // Clear previous debounce
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current);
                }

                // Debounce search for better UX (500ms delay)
                debounceRef.current = setTimeout(() => {
                    onSearch?.(value);
                }, 500);
            },
            [onSearch],
        );

        const handleDelete = useCallback(() => {
            if (selectedCount > 0 && onDelete) {
                onDelete(selectedSourceIds);
            }
        }, [selectedSourceIds, selectedCount, onDelete]);

        const handleConfigureMigration = useCallback(() => {
            if (selectedCount > 0 && onConfigureMigration) {
                onConfigureMigration(selectedSourceIds);
            }
        }, [selectedCount, selectedSourceIds, onConfigureMigration]);

        const handleInspectSources = useCallback(() => {
            if (selectedCount > 0 && onInspectSources) {
                onInspectSources(selectedSourceIds);
            }
        }, [selectedCount, selectedSourceIds, onInspectSources]);

        const handleConfigureInSitu = useCallback(() => {
            if (selectedCount > 0 && onConfigureInSitu) {
                onConfigureInSitu(selectedSourceIds);
            }
        }, [selectedCount, selectedSourceIds, onConfigureInSitu]);

        const handleAddTags = useCallback(() => {
            if (selectedCount > 0 && onAddTags) {
                onAddTags(selectedSourceIds);
            }
        }, [selectedCount, selectedSourceIds, onAddTags]);

        const handleAssignMoveGroup = useCallback(() => {
            if (selectedCount > 0 && onAssignMoveGroup) {
                onAssignMoveGroup(selectedSourceIds);
            }
        }, [selectedCount, selectedSourceIds, onAssignMoveGroup]);

        const handleAddSource = useCallback(() => {
            onAddSource?.();
        }, [onAddSource]);

        const handleUploadSources = useCallback(() => {
            onUploadSources?.();
        }, [onUploadSources]);

        const handleRunCsvMigration = useCallback(() => {
            onRunCsvMigration?.();
        }, [onRunCsvMigration]);

        // Cleanup debounce on unmount
        useEffect(() => {
            return () => {
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current);
                }
            };
        }, []);

        return (
            <>
                <Row gutter={[8, 8]}>
                    {/* Add Source Button */}
                    <Col>
                        <Button
                            onClick={handleAddSource}
                            className="top-bar-acton-btn"
                            type="primary"
                            icon={<PlusOutlined />}
                        >
                            Add Source
                        </Button>
                    </Col>
                    {/* Upload Multiple Sources Button */}
                    <Col>
                        <Button onClick={handleUploadSources} className="top-bar-acton-btn" type="primary">
                            Upload Multiple Sources
                        </Button>
                    </Col>
                    {/* Other action buttons */}
                    {actionButtons.map(({ key, label, onClick }) => (
                        <Col key={key}>
                            <Button
                                onClick={() => {
                                    if (onClick) {
                                        onClick();
                                    } else if (key === 'runCSVMigration') {
                                        handleRunCsvMigration();
                                    } else if (key === 'refreshSources') {
                                        onRefreshSources?.();
                                    } else {
                                        window[key]?.();
                                    }
                                }}
                                className="top-bar-acton-btn"
                                type="primary"
                            >
                                {label}
                            </Button>
                        </Col>
                    ))}
                </Row>
                <Row className={'margin-top-10'} gutter={[8, 8]} wrap={false} justify="middle">
                    {/* Delete Button */}
                    <Col>
                        <Tooltip
                            title={
                                selectedCount === 0
                                    ? 'Select sources to delete'
                                    : `Delete ${selectedCount} selected source${selectedCount > 1 ? 's' : ''}`
                            }
                        >
                            <Button
                                icon={<DeleteOutlined />}
                                danger
                                disabled={selectedCount === 0}
                                onClick={handleDelete}
                                size="middle"
                            />
                        </Tooltip>
                    </Col>
                    {/* Add Tags Button */}
                    <Col>
                        <Tooltip
                            title={
                                selectedCount === 0
                                    ? 'Select sources to add tags'
                                    : `Add tags to ${selectedCount} selected source${selectedCount > 1 ? 's' : ''}`
                            }
                        >
                            <Button
                                icon={<TagOutlined />}
                                disabled={selectedCount === 0}
                                onClick={handleAddTags}
                                size="middle"
                                color="primary"
                                variant="outlined"
                            />
                        </Tooltip>
                    </Col>
                    {/* Assign Move Group Button */}
                    <Col>
                        <Tooltip
                            title={
                                selectedCount === 0
                                    ? 'Select sources to assign move group'
                                    : `Assign move group to ${selectedCount} selected source${selectedCount > 1 ? 's' : ''}`
                            }
                        >
                            <Button
                                disabled={selectedCount === 0}
                                onClick={handleAssignMoveGroup}
                                size="middle"
                                color="primary"
                                variant="outlined"
                            >
                                Assign Move Groups
                            </Button>
                        </Tooltip>
                    </Col>
                    {/* Configure Migration Button */}
                    <Col>
                        <Tooltip
                            title={
                                selectedCount === 0
                                    ? 'Select sources to configure migration'
                                    : `Configure migration for ${selectedCount} selected source${selectedCount > 1 ? 's' : ''}`
                            }
                        >
                            <Button
                                disabled={selectedCount === 0}
                                onClick={handleConfigureMigration}
                                size="middle"
                                color="primary"
                                variant="outlined"
                            >
                                Inspect & Migrate
                            </Button>
                        </Tooltip>
                    </Col>
                    {/* Inspect Sources Button */}
                    <Col>
                        <Tooltip
                            title={
                                selectedCount === 0
                                    ? 'Select sources to inspect'
                                    : `Inspect ${selectedCount} selected source${selectedCount > 1 ? 's' : ''}`
                            }
                        >
                            <Button
                                disabled={selectedCount === 0}
                                onClick={handleInspectSources}
                                size="middle"
                                color="primary"
                                variant="outlined"
                            >
                                Inspect Sources
                            </Button>
                        </Tooltip>
                    </Col>
                    {/* Configure In-Situ Upgrade Button */}
                    <Col>
                        <Tooltip
                            title={
                                selectedCount === 0
                                    ? 'Select sources to configure in-situ upgrade'
                                    : `Configure In-Situ Upgrade for ${selectedCount} selected source${selectedCount > 1 ? 's' : ''}`
                            }
                        >
                            <Button
                                disabled={selectedCount === 0}
                                onClick={handleConfigureInSitu}
                                size="middle"
                                color="primary"
                                variant="outlined"
                            >
                                Configure In-Situ Upgrade
                            </Button>
                        </Tooltip>
                    </Col>
                </Row>
                <Row className={'margin-top-10'} gutter={[8, 8]} wrap={false} align="middle">
                    <Col flex="500px">
                        <Search
                            placeholder="Search by Source IP, Source Name, Tag or Move Group Name"
                            value={searchValue}
                            onChange={handleInputChange}
                            onSearch={handleSearch}
                            allowClear
                            size="middle"
                            style={{ width: '100%' }}
                        />
                    </Col>
                </Row>
            </>
        );
    },
);
